export const COMMANDER_CATEGORIES = [
  { value: "CASUAL", label: "Casual" },
  { value: "OPTIMIZED", label: "Optimized" },
  { value: "COMPETITIVE", label: "Competitive" },
] as const;

export type CommanderCategory =
  (typeof COMMANDER_CATEGORIES)[number]["value"];

export interface CommanderPairingPlayer {
  userId: string;
  category: CommanderCategory;
  points?: number;
}

export interface CommanderPairingPod {
  category: CommanderCategory;
  playerIds: string[];
}

export interface CommanderPreviousPod {
  playerIds: string[];
}

const categoryIndex = (category: CommanderCategory) =>
  COMMANDER_CATEGORIES.findIndex((option) => option.value === category);

const countRepeatedPairs = (
  playerIds: string[],
  previousPods: CommanderPreviousPod[]
) => {
  let repeats = 0;
  for (let first = 0; first < playerIds.length; first += 1) {
    for (let second = first + 1; second < playerIds.length; second += 1) {
      if (
        previousPods.some(
          (pod) =>
            pod.playerIds.includes(playerIds[first]) &&
            pod.playerIds.includes(playerIds[second])
        )
      ) {
        repeats += 1;
      }
    }
  }
  return repeats;
};

const minimizeRepeatOpponents = (
  pods: CommanderPairingPod[],
  players: CommanderPairingPlayer[],
  previousPods: CommanderPreviousPod[]
) => {
  const playerById = new Map(players.map((player) => [player.userId, player]));
  const optimized = pods.map((pod) => ({
    ...pod,
    playerIds: [...pod.playerIds],
  }));
  const totalRepeats = () =>
    optimized.reduce(
      (sum, pod) => sum + countRepeatedPairs(pod.playerIds, previousPods),
      0
    );

  let improved = true;
  while (improved) {
    improved = false;
    const startingRepeats = totalRepeats();
    for (let firstPod = 0; firstPod < optimized.length; firstPod += 1) {
      for (
        let secondPod = firstPod + 1;
        secondPod < optimized.length;
        secondPod += 1
      ) {
        for (
          let firstSeat = 0;
          firstSeat < optimized[firstPod].playerIds.length;
          firstSeat += 1
        ) {
          for (
            let secondSeat = 0;
            secondSeat < optimized[secondPod].playerIds.length;
            secondSeat += 1
          ) {
            const firstId = optimized[firstPod].playerIds[firstSeat];
            const secondId = optimized[secondPod].playerIds[secondSeat];
            const firstPlayer = playerById.get(firstId)!;
            const secondPlayer = playerById.get(secondId)!;
            if (
              firstPlayer.category !== secondPlayer.category ||
              (firstPlayer.points || 0) !== (secondPlayer.points || 0)
            ) {
              continue;
            }

            optimized[firstPod].playerIds[firstSeat] = secondId;
            optimized[secondPod].playerIds[secondSeat] = firstId;
            if (totalRepeats() < startingRepeats) {
              improved = true;
              break;
            }
            optimized[firstPod].playerIds[firstSeat] = firstId;
            optimized[secondPod].playerIds[secondSeat] = secondId;
          }
          if (improved) break;
        }
        if (improved) break;
      }
      if (improved) break;
    }
  }
  return optimized;
};

export function generateCommanderPods(
  players: CommanderPairingPlayer[],
  previousPods: CommanderPreviousPod[] = []
): CommanderPairingPod[] {
  if (players.length < 3 || players.length === 5) {
    throw new Error(
      "At least three players are required, and five players cannot form valid 3–4 player pods."
    );
  }

  const orderedPlayers = [...players].sort(
    (a, b) =>
      categoryIndex(a.category) - categoryIndex(b.category) ||
      (b.points || 0) - (a.points || 0) ||
      a.userId.localeCompare(b.userId)
  );
  const memo = new Map<number, { cost: number; pods: CommanderPairingPod[] }>();

  const solve = (
    offset: number
  ): { cost: number; pods: CommanderPairingPod[] } | null => {
    if (offset === orderedPlayers.length) return { cost: 0, pods: [] };
    if (memo.has(offset)) return memo.get(offset)!;

    let best: { cost: number; pods: CommanderPairingPod[] } | null = null;
    for (const size of [4, 3]) {
      const members = orderedPlayers.slice(offset, offset + size);
      if (members.length !== size) continue;

      const categories = new Set(members.map((member) => member.category));
      const pointValues = members.map((member) => member.points || 0);
      const repeatedPairs = countRepeatedPairs(
        members.map((member) => member.userId),
        previousPods
      );
      const remainder = solve(offset + size);
      if (!remainder) continue;

      const cost =
        (categories.size - 1) * 10000 +
        repeatedPairs * 100 +
        (Math.max(...pointValues) - Math.min(...pointValues)) * 10 +
        (size === 3 ? 1 : 0) +
        remainder.cost;
      const pod: CommanderPairingPod = {
        category: members[0].category,
        playerIds: members.map((member) => member.userId),
      };
      const candidate = { cost, pods: [pod, ...remainder.pods] };
      if (!best || candidate.cost < best.cost) best = candidate;
    }

    if (best) memo.set(offset, best);
    return best;
  };

  const result = solve(0);
  if (!result) {
    throw new Error("Unable to form valid Commander pods.");
  }
  return minimizeRepeatOpponents(result.pods, players, previousPods);
}

export function getCompetitionRanks<T>(
  entries: T[],
  getPoints: (entry: T) => number
): Array<T & { rank: number }> {
  let previousPoints: number | null = null;
  let previousRank = 0;
  return entries.map((entry, index) => {
    const points = getPoints(entry);
    const rank =
      previousPoints !== null && points === previousPoints
        ? previousRank
        : index + 1;
    previousPoints = points;
    previousRank = rank;
    return { ...entry, rank };
  });
}
